import React, { useState, useEffect } from 'react';
import { Card, Typography, Form, Input, Button, Row, Col, Alert, Space } from 'antd';
import { useToast } from '../../../contexts/ToastContext';
import { SaveOutlined, LockOutlined, MailOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { ChangePasswordRequest } from '../../../types';
import { useSearchParams } from 'react-router-dom';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import { useAuth } from '../../../contexts/AuthContext';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';

const { Title, Text } = Typography;

const SettingsTab: React.FC = () => {
  const toast = useToast();
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [passwordForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [verificationForm] = Form.useForm();
  const [newEmail, setNewEmail] = useState<string>('');
  const [verificationSent, setVerificationSent] = useState(false);

  // Check URL params for token and code
  useEffect(() => {
    const token = searchParams.get('token');
    const code = searchParams.get('code');
    if (token && code) {
      setVerificationSent(true);
      verificationForm.setFieldsValue({ token, code });
      const next = new URLSearchParams(searchParams);
      next.delete('token');
      next.delete('code');
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, verificationForm, setSearchParams]);

  const changePasswordMutation = useMutation({
    mutationFn: (values: ChangePasswordRequest) =>
      apiService.changeMentorPassword(values),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Şifre başarıyla değiştirildi.');
        passwordForm.resetFields();
      } else {
        toast.error(response.message || 'Şifre değiştirilirken hata oluştu.');
      }
    },
    onError: () => toast.error('Şifre değiştirilirken hata oluştu.'),
  });

  const requestVerificationMutation = useMutation({
    mutationFn: (data: { newEmail: string }) =>
      apiService.requestMentorEmailVerification(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Doğrulama e-postası gönderildi. Lütfen e-posta kutunuzu kontrol edin.');
        setVerificationSent(true);
        emailForm.setFieldsValue({ newEmail: '' });
      } else {
        toast.error(response.message || 'Doğrulama e-postası gönderilirken hata oluştu.');
      }
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Doğrulama e-postası gönderilirken hata oluştu.';
      toast.error(message);
    },
  });

  const verifyEmailMutation = useMutation({
    mutationFn: (data: { token: string; code: string; password: string }) =>
      apiService.verifyMentorEmail(data),
    onSuccess: async (response) => {
      if (response.success) {
        toast.success('E-posta adresi başarıyla değiştirildi.');
        await refreshUser();
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.profile });
        verificationForm.resetFields();
        setVerificationSent(false);
        setNewEmail('');
      } else {
        toast.error(response.message || 'E-posta doğrulanırken hata oluştu.');
      }
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'E-posta doğrulanırken hata oluştu.';
      toast.error(message);
    },
  });

  const handlePasswordChange = (values: ChangePasswordRequest) => {
    changePasswordMutation.mutate(values);
  };

  const handleRequestVerification = async (values: { newEmail: string }) => {
    setNewEmail(values.newEmail);
    requestVerificationMutation.mutate({ newEmail: values.newEmail });
  };

  const handleVerifyEmail = (values: { token: string; code: string; password: string }) => {
    verifyEmailMutation.mutate(values);
  };

  return (
    <PanelTabCard
      title="Güvenlik Ayarları"
      description="Şifrenizi ve e-posta adresinizi güncelleyin."
    >
      <Card style={{ marginBottom: '24px' }}>
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordChange}
        >
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Mevcut Şifre"
                name="currentPassword"
                rules={[{ required: true, message: 'Mevcut şifre gereklidir' }]}
              >
                <Input.Password placeholder="Mevcut şifrenizi giriniz" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Yeni Şifre"
                name="newPassword"
                rules={[
                  { required: true, message: 'Yeni şifre gereklidir' },
                  { min: 6, message: 'Şifre en az 6 karakter olmalıdır' }
                ]}
              >
                <Input.Password placeholder="Yeni şifrenizi giriniz" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={changePasswordMutation.isPending}
              icon={<SaveOutlined />}
            >
              Şifreyi Değiştir
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <MailOutlined style={{ marginRight: '8px' }} />
            <Title level={4} style={{ margin: 0 }}>E-posta Değiştir</Title>
          </div>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Mevcut giriş e-postanız: ${user?.email || '-'}`}
          description="E-posta değiştirmek için yeni adresinize doğrulama maili gönderilir."
        />
        {!verificationSent ? (
          <Form
            form={emailForm}
            layout="vertical"
            onFinish={handleRequestVerification}
          >
            <Form.Item
              label="Yeni E-posta Adresi"
              name="newEmail"
              rules={[
                { required: true, message: 'Yeni e-posta adresi gereklidir' },
                { type: 'email', message: 'Geçerli bir e-posta adresi giriniz' },
                {
                  validator: (_, value) => {
                    if (
                      value &&
                      user?.email &&
                      value.trim().toLowerCase() === user.email.trim().toLowerCase()
                    ) {
                      return Promise.reject(new Error('Yeni e-posta mevcut adresinizle aynı olamaz.'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input 
                placeholder="yeni@email.com" 
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={requestVerificationMutation.isPending}
                icon={<MailOutlined />}
              >
                Doğrulama E-postası Gönder
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <>
            <Alert
              message="Doğrulama E-postası Gönderildi"
              description={
                <div>
                  <Text>Yeni e-posta adresinize ({newEmail}) doğrulama kodu gönderildi.</Text>
                  <br />
                  <Text type="secondary">Lütfen e-posta kutunuzu kontrol edin ve aşağıdaki formu doldurun.</Text>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />
            <Form
              form={verificationForm}
              layout="vertical"
              onFinish={handleVerifyEmail}
            >
              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Doğrulama Kodu"
                    name="code"
                    rules={[
                      { required: true, message: 'Doğrulama kodu gereklidir' },
                      { len: 6, message: 'Doğrulama kodu 6 haneli olmalıdır' }
                    ]}
                  >
                    <Input placeholder="123456" maxLength={6} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Mevcut Şifre"
                    name="password"
                    rules={[{ required: true, message: 'Şifre gereklidir' }]}
                  >
                    <Input.Password placeholder="Şifrenizi giriniz" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Token (E-posta linkinden otomatik doldurulur)"
                name="token"
                rules={[{ required: true, message: 'Token gereklidir' }]}
              >
                <Input placeholder="Token" />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={verifyEmailMutation.isPending}
                    icon={<CheckCircleOutlined />}
                  >
                    E-postayı Doğrula ve Değiştir
                  </Button>
                  <Button 
                    onClick={() => {
                      setVerificationSent(false);
                      setNewEmail('');
                      verificationForm.resetFields();
                    }}
                  >
                    İptal
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        )}
      </Card>
    </PanelTabCard>
  );
};

export default SettingsTab;
